// admin-analytics-expansion P1 (D3) — Per-location breakdown table.
//
// Purely presentational: renders `OrgAnalyticsResponse.byLocation[]` rows.
// `locationNameById` is a client-side id->name join built by AnalyticsPage
// from the already-fetched `getMyLocations()` response (D3) — this
// component itself makes no network calls, so switching to the Organization
// tab never triggers an extra request beyond `getOrgAnalytics`/`getMyLocations`.
// A row whose `locationId` is not in the map (e.g. a location removed after
// the ticket was checked in) falls back to an em dash.

import { useTranslation } from '@jq4race/i18n';
import { DEFAULT_CURRENCY, type LocationAnalyticsBreakdownRow } from '@jq4race/shared';
import {
  formatAvgSeconds,
  formatCents,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from '@jq4race/ui';

const CURRENCY_FALLBACK = DEFAULT_CURRENCY;

export interface LocationBreakdownTableProps {
  rows: LocationAnalyticsBreakdownRow[] | undefined;
  locationNameById: Map<string, string>;
}

export function LocationBreakdownTable({ rows, locationNameById }: LocationBreakdownTableProps) {
  const { t } = useTranslation('analytics');
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('table.locationHeader')}</TableHead>
          <TableHead align="right">{t('kpi.volume')}</TableHead>
          <TableHead align="right">{t('kpi.revenue')}</TableHead>
          <TableHead align="right">{t('kpi.avgRetrievalTime')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows === undefined ? (
          <TableSkeleton columns={4} />
        ) : rows.length === 0 ? (
          <TableEmpty colSpan={4} message={t('emptyStates.noLocationDataForRange')} />
        ) : (
          rows.map((row) => (
            <TableRow key={row.locationId}>
              <TableCell>{locationNameById.get(row.locationId) ?? '—'}</TableCell>
              <TableCell align="right">{row.volume}</TableCell>
              <TableCell align="right">
                {formatCents(row.netRevenueCents, CURRENCY_FALLBACK)}
              </TableCell>
              <TableCell align="right">
                {row.avgRetrievalSeconds !== null ? formatAvgSeconds(row.avgRetrievalSeconds) : '—'}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
